import { useRef, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, Phone, ChevronDown, MapPin, Wine } from "lucide-react";
import { useLang } from "@/App";
import { copy, CONTACT } from "@/i18n";
import { isOpenNow, scrollToId } from "@/utils";
import WhatsAppIcon from "@/components/WhatsAppIcon";

const MaskedLine = ({ children, delay, className = "" }) => (
    <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
        <motion.span
            className={`block ${className}`}
            initial={{ y: "115%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

const Hero = () => {
    const { lang } = useLang();
    const c = copy[lang].hero;
    const ref = useRef(null);
    const videoRef = useRef(null);

    useEffect(() => {
        videoRef.current?.play().catch(() => {});
    }, []);

    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const markY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
    const open = isOpenNow();

    return (
        <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex items-end overflow-hidden">
            <motion.div style={{ y: markY }} className="absolute inset-0 overflow-hidden">
                <video
                    ref={videoRef}
                    data-testid="hero-video"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    poster="/media/neon.webp"
                    className="w-full h-[118%] object-cover"
                >
                    <source src="/media/hero.webm" type="video/webm" />
                    <source src="/media/hero.mp4" type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-noir via-noir/65 to-noir/35" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_75%_20%,rgba(230,0,35,0.18),transparent_55%)]" />
            </motion.div>

            <motion.div style={{ opacity: fade }} className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-8 pb-16 sm:pb-24 pt-40">
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15, duration: 0.7 }}
                    className="flex flex-wrap items-center gap-3 mb-6"
                >
                    <span
                        data-testid="hero-status-chip"
                        className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-noir/50 backdrop-blur px-4 py-1.5 text-[11px] font-mono uppercase tracking-[0.2em]"
                    >
                        <span className={`w-2 h-2 rounded-full ${open ? "bg-emerald-400" : "bg-crimson"} animate-pulse-glow`} />
                        {open ? c.openNow : c.closedNow}
                    </span>
                    <span className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-neutral-400">
                        <MapPin size={12} className="text-crimson" />
                        {c.eyebrow}
                    </span>
                </motion.div>

                <h1 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-4xl sm:text-6xl lg:text-8xl">
                    <MaskedLine delay={0.25}>{c.line1}</MaskedLine>
                    <MaskedLine delay={0.4} className="text-crimson-bright">
                        {c.line2}
                    </MaskedLine>
                    <MaskedLine delay={0.55} className="text-outline">
                        {c.line3}
                    </MaskedLine>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.85, duration: 0.7 }}
                    className="mt-6 max-w-xl font-serif italic text-lg sm:text-2xl text-brass/90 leading-relaxed"
                >
                    {c.tagline}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1, duration: 0.7 }}
                    className="mt-8 flex flex-wrap items-center gap-4"
                >
                    <a
                        data-testid="hero-call-cta"
                        href={CONTACT.phoneHref}
                        className="group inline-flex items-center gap-3 rounded-full bg-crimson hover:bg-crimson-bright transition-colors text-white font-bold uppercase tracking-widest text-sm px-7 py-4 glow-red"
                    >
                        <Phone size={16} className="transition-transform group-hover:rotate-12" />
                        {c.ctaCall}
                    </a>
                    <a
                        data-testid="hero-whatsapp-cta"
                        href={CONTACT.whatsapp}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-3 rounded-full border border-white/20 hover:border-[#25D366] text-neutral-100 hover:text-[#25D366] transition-colors font-bold uppercase tracking-widest text-sm px-7 py-4"
                    >
                        <WhatsAppIcon size={17} />
                        {c.ctaWhats}
                    </a>
                    <button
                        data-testid="hero-menu-cta"
                        onClick={() => scrollToId("menu")}
                        className="inline-flex items-center gap-3 rounded-full border border-white/20 hover:border-brass text-neutral-100 hover:text-brass transition-colors font-bold uppercase tracking-widest text-sm px-7 py-4"
                    >
                        {c.ctaMenu}
                    </button>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.2, duration: 0.8 }}
                    className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-neutral-400"
                >
                    <span data-testid="hero-rating" className="inline-flex items-center gap-2">
                        <Star size={15} className="fill-brass text-brass" />
                        <strong className="text-white font-heading">4.7</strong> · 172 {c.metricReviews}
                    </span>
                    <span className="inline-flex items-center gap-2">
                        <Wine size={15} className="text-brass" />
                        <strong className="text-white font-heading">50+</strong> {c.metricWines}
                    </span>
                    <span className="inline-flex items-center gap-2">
                        <strong className="text-white font-heading">CHF 20–30</strong> {c.metricPrice}
                    </span>
                </motion.div>
            </motion.div>

            <motion.button
                data-testid="hero-scroll-indicator"
                onClick={() => scrollToId("about")}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6 }}
                className="absolute bottom-6 right-6 sm:right-10 z-10 flex flex-col items-center gap-1 text-neutral-500 hover:text-crimson-bright transition-colors"
                aria-label={c.scroll}
            >
                <span className="text-[10px] font-mono uppercase tracking-[0.3em]">{c.scroll}</span>
                <ChevronDown size={18} className="animate-bounce" />
            </motion.button>
        </section>
    );
};

export default Hero;
